import { useCallback, useEffect, useRef } from "react";
import { useHakikisha } from "../../../../hooks/queries/useBulkUploadsQueries";
import useManualEntryStore from "../../../../stores/manualEntryStore";
import { MANUAL_ENTRY_CHANNELS } from "../config/manualEntryConfig";
import { VERIFICATION_STATUS } from "../config/verificationStatus";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";

export function useBankVerification({ paymentChannel, accountNumber, bankCode, setValue }) {
  const bankVerificationStatus = useManualEntryStore((s) => s.bankVerificationStatus);

  const { mutate, isPending: isVerifying } = useHakikisha();
  const pendingAccountRef = useRef(null);
  const prevBankCodeRef = useRef(undefined);

  const accountNumberRef = useRef(accountNumber);
  accountNumberRef.current = accountNumber;

  const verify = useCallback(
    (accNum) => {
      if (!bankCode) return;
      pendingAccountRef.current = accNum;

      mutate(
        { paymentChannel: MANUAL_ENTRY_CHANNELS.BANK, accountNumber: accNum, bankCode },
        {
          onSuccess: (data) => {
            if (pendingAccountRef.current !== accNum) return;
            const store = useManualEntryStore.getState();

            if (data.success) {
              store.setBankVerification({
                status: VERIFICATION_STATUS.VERIFIED,
                accountNumber: accNum,
                bankCode,
                bankName: null,
                message: null,
              });
              setValue("recipientName", data.recipientName || "");
            } else {
              store.setBankVerification({
                status: VERIFICATION_STATUS.UNVERIFIED,
                accountNumber: accNum,
                bankCode,
                bankName: null,
                message: data.message || "Verification failed",
              });
              setValue("recipientName", "");
            }
          },
          onError: (error) => {
            if (pendingAccountRef.current !== accNum) return;
            const message = extractAxiosErrorMessage(error, "Could not verify account");
            useManualEntryStore.getState().setBankVerification({
              status: VERIFICATION_STATUS.UNVERIFIED,
              accountNumber: accNum,
              bankCode,
              bankName: null,
              message,
            });
            setValue("recipientName", "");
          },
        },
      );
    },
    [bankCode, mutate, setValue],
  );

  // When bankCode changes, reset verification and re-verify if account number already filled
  useEffect(() => {
    const prevBankCode = prevBankCodeRef.current;
    prevBankCodeRef.current = bankCode;

    if (prevBankCode === undefined || prevBankCode === bankCode) return;

    useManualEntryStore.getState().resetBankVerification();
    setValue("recipientName", "");

    const currentAccount = accountNumberRef.current?.trim();
    if (bankCode && currentAccount && currentAccount.length >= 8) {
      verify(currentAccount);
    }
  }, [bankCode, verify, setValue]);

  // When channel switches away from Bank, reset bank verification
  useEffect(() => {
    if (paymentChannel !== MANUAL_ENTRY_CHANNELS.BANK) {
      useManualEntryStore.getState().resetBankVerification();
    }
  }, [paymentChannel]);

  const handleAccountNumberChange = useCallback(
    (value) => {
      const { verifiedAccountNumber, bankVerificationStatus: status } =
        useManualEntryStore.getState();
      if (value !== verifiedAccountNumber && status !== VERIFICATION_STATUS.IDLE) {
        useManualEntryStore.getState().resetBankVerification();
        setValue("recipientName", "");
      }
    },
    [setValue],
  );

  const handleAccountNumberBlur = useCallback(
    (value) => {
      if (!paymentChannel || paymentChannel !== MANUAL_ENTRY_CHANNELS.BANK) return;
      if (!bankCode) return;
      const { verifiedAccountNumber } = useManualEntryStore.getState();
      if (value === verifiedAccountNumber) return;
      const trimmed = value?.trim() ?? "";
      if (trimmed.length >= 8) {
        verify(trimmed);
      }
    },
    [paymentChannel, bankCode, verify],
  );

  const isVerified = bankVerificationStatus === VERIFICATION_STATUS.VERIFIED;
  const isUnverified = bankVerificationStatus === VERIFICATION_STATUS.UNVERIFIED;

  return {
    handleAccountNumberChange,
    handleAccountNumberBlur,
    isVerifying,
    isVerified,
    isUnverified,
  };
}
